
import React from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import { Eye } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";

interface NetworkControlsProps {
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleCenterView: () => void;
  collectionLinkOpacity: number;
  tagLinkOpacity: number;
  handleCollectionOpacityChange: (value: number[]) => void;
  handleTagOpacityChange: (value: number[]) => void;
  collectionLinksCount: number;
  tagLinksCount: number;
}

const NetworkControls: React.FC<NetworkControlsProps> = ({
  handleZoomIn,
  handleZoomOut,
  handleCenterView,
  collectionLinkOpacity,
  tagLinkOpacity,
  handleCollectionOpacityChange,
  handleTagOpacityChange,
  collectionLinksCount,
  tagLinksCount
}) => {
  return (
    <>
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                onClick={handleZoomIn}
                className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
              >
                <ZoomIn size={20} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Zoom In</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                onClick={handleZoomOut}
                className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
              >
                <ZoomOut size={20} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Zoom Out</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                onClick={handleCenterView}
                className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" fill="none" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>Center View</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <div className="absolute left-4 bottom-4 z-10 bg-transparent backdrop-blur-sm p-4 rounded-lg shadow-md w-64">
        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye size={16} />
                <Label htmlFor="collection-opacity" className="font-medium">Collection Links</Label>
              </div>
              <span className="text-xs text-gray-500">{collectionLinksCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">{collectionLinkOpacity}%</span>
              <Slider
                id="collection-opacity"
                value={[collectionLinkOpacity]}
                min={0}
                max={100}
                step={5}
                onValueChange={handleCollectionOpacityChange}
                className="flex-1"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye size={16} />
                <Label htmlFor="tag-opacity" className="font-medium">Tag Links</Label>
              </div>
              <span className="text-xs text-gray-500">{tagLinksCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">{tagLinkOpacity}%</span>
              <Slider
                id="tag-opacity"
                value={[tagLinkOpacity]}
                min={0}
                max={100}
                step={5}
                onValueChange={handleTagOpacityChange}
                className="flex-1"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NetworkControls;
